import { ScheduleSuggestion, SuggestionParameters, TimeSlot, AnalysisContext } from '../types/AutoScheduling';
import ScheduleEntry from '../models/ScheduleEntry';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import { availabilityService } from './AvailabilityService';
import SuggestionScoringService from './SuggestionScoringService';
import { formatTimeFromMinutes } from '../utils/helpers';

/**
 * Core service for generating schedule suggestions
 */
class AutoSchedulingService {
  private suggestionScoringService: SuggestionScoringService;
  private readonly BATCH_SIZE = 50; // Process suggestions in batches to avoid UI blocking
  private readonly MAX_SUGGESTIONS = 1000; // Limit total suggestions for performance
  private readonly builtIndexWeeks: Set<string> = new Set();

  constructor() {
    this.suggestionScoringService = new SuggestionScoringService();
  }

  /**
   * Generate schedule suggestions based on parameters with comprehensive error handling
   */
  async generateSuggestions(
    parameters: SuggestionParameters,
    baseScheduleItems: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    onProgress?: (progress: { current: number; total: number; message: string }) => void
  ): Promise<ScheduleSuggestion[]> {
    console.log('[AutoSchedulingService] Generating suggestions with parameters:', parameters);

    try {
      // Validate parameters with detailed error messages
      this.validateParameters(parameters);

      // Validate context data
      this.validateContextData(baseScheduleItems, teachers, groups, subjects, parameters);

      // Generate time slots with error handling
      let timeSlots: TimeSlot[];
      try {
        timeSlots = this.generateTimeSlots(
          parameters.minHour,
          parameters.maxHour,
          parameters.duration
        );
      } catch (error) {
        throw new Error(`Failed to generate time slots: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      if (timeSlots.length === 0) {
        throw new Error(`No valid time slots found for the specified time range (${parameters.minHour}:00 - ${parameters.maxHour}:00) and duration (${parameters.duration} minutes). Please adjust your time range or duration.`);
      }

      console.log(`[AutoSchedulingService] Generated ${timeSlots.length} time slots`);

      // Generate week patterns (weeks do not need to be consecutive) with error handling
      let weekPatterns: number[][];
      try {
        weekPatterns = this.generateWeekPatterns(parameters.weeks, parameters.weeksToSchedule);
      } catch (error) {
        throw new Error(`Failed to generate week patterns: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      if (weekPatterns.length === 0) {
        throw new Error(`No valid week patterns found for ${parameters.weeksToSchedule}-week scheduling with selected weeks: ${parameters.weeks.join(', ')}. Please select more weeks or reduce the number of weeks to schedule.`);
      }
      
      console.log(`[AutoSchedulingService] Generated ${weekPatterns.length} week patterns for ${parameters.weeksToSchedule}-week scheduling`);

      // Calculate total combinations for progress tracking
      const totalCombinations = weekPatterns.length * 
                               parameters.days.length * 
                               timeSlots.length * 
                               parameters.rooms.length;

      console.log(`[AutoSchedulingService] Total combinations to analyze: ${totalCombinations.toLocaleString()}`);

      // Filter out the current item being edited to avoid self-conflicts
      const filteredBaseScheduleItems = parameters.excludeItemId 
        ? baseScheduleItems.filter(item => item.id?.toString() !== parameters.excludeItemId?.toString())
        : baseScheduleItems;

      if (parameters.excludeItemId) {
        const excludedCount = baseScheduleItems.length - filteredBaseScheduleItems.length;
        console.log(`[AutoSchedulingService] Filtered out ${excludedCount} item(s) with ID ${parameters.excludeItemId} to avoid self-conflicts`);
      }

      const suggestions: ScheduleSuggestion[] = [];
      const context: AnalysisContext = {
        baseScheduleItems: filteredBaseScheduleItems,
        teachers,
        groups,
        subjects
      };

      // Create all combinations for batch processing
      const combinations: Array<{
        weekPattern: number[];
        day: string;
        timeSlot: TimeSlot;
        room: string;
      }> = [];

      // Generate ALL possible combinations (no stop limit)
      for (const weekPattern of weekPatterns) {
        for (const day of parameters.days) {
          for (const timeSlot of timeSlots) {
            for (const room of parameters.rooms) {
              combinations.push({ weekPattern, day, timeSlot, room });
            }
          }
        }
      }

      console.log(`[AutoSchedulingService] Processing ALL ${combinations.length} combinations in batches of ${this.BATCH_SIZE}`);

      // Process combinations in batches with enhanced error handling
      let processedCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (let i = 0; i < combinations.length; i += this.BATCH_SIZE) {
        const batch = combinations.slice(i, i + this.BATCH_SIZE);
        
        // Process batch with individual error handling
        const batchPromises = batch.map(async ({ weekPattern, day, timeSlot, room }) => {
          try {
            return await this.analyzeWeekPattern(
              day,
              timeSlot.timeStart,
              timeSlot.timeEnd,
              room,
              weekPattern,
              parameters,
              context
            );
          } catch (error) {
            errorCount++;
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(`[AutoSchedulingService] Error analyzing ${day} ${timeSlot.timeStart}-${timeSlot.timeEnd} in ${room}:`, errorMessage);
            
            // Collect unique error messages for user feedback
            if (!errors.includes(errorMessage) && errors.length < 5) {
              errors.push(errorMessage);
            }
            
            return null;
          }
        });

        let batchResults: (ScheduleSuggestion | null)[];
        try {
          batchResults = await Promise.all(batchPromises);
        } catch (error) {
          // Handle batch-level failures
          console.error('[AutoSchedulingService] Batch processing failed:', error);
          throw new Error(`Analysis failed during batch processing: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        
        // Add valid suggestions
        for (const result of batchResults) {
          if (result) {
            suggestions.push(result);
          }
        }

        processedCount += batch.length;

        // Report progress with error information
        if (onProgress) {
          const progressMessage = errorCount > 0 
            ? `Analyzed ${processedCount} of ${combinations.length} time slots (${errorCount} errors)`
            : `Analyzed ${processedCount} of ${combinations.length} time slots`;
            
          onProgress({
            current: processedCount,
            total: combinations.length,
            message: progressMessage
          });
        }

        // Yield control to prevent UI blocking
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // Handle case where no suggestions were generated
      if (suggestions.length === 0) {
        let noSuggestionsMessage = 'No schedule suggestions could be generated with the current parameters.';
        
        if (errorCount > 0) {
          noSuggestionsMessage += ` ${errorCount} analysis errors occurred.`;
          if (errors.length > 0) {
            noSuggestionsMessage += ` Common issues: ${errors.slice(0, 3).join('; ')}.`;
          }
        }
        
        noSuggestionsMessage += '\n\nTry adjusting your parameters:\n';
        noSuggestionsMessage += '• Select different weeks or days\n';
        noSuggestionsMessage += '• Expand your time range\n';
        noSuggestionsMessage += '• Include more rooms\n';
        noSuggestionsMessage += '• Check if the teacher and groups are available';
        
        throw new Error(noSuggestionsMessage);
      }

      console.log(`[AutoSchedulingService] Generated ${suggestions.length} suggestions from ${processedCount} combinations (${errorCount} errors)`);

      // Sort suggestions by conflict score (ascending), then by time, then by room
      let sortedSuggestions: ScheduleSuggestion[];
      try {
        sortedSuggestions = this.rankSuggestions(suggestions);
      } catch (error) {
        console.error('[AutoSchedulingService] Error ranking suggestions:', error);
        // Fallback to unsorted suggestions
        sortedSuggestions = suggestions;
      }

      // Smart selection: Pick top 10 with random selection among similar low-conflict options
      const topSuggestions = this.selectTopSuggestionsWithRandomization(sortedSuggestions, 10);
      
      console.log(`[AutoSchedulingService] Returning top ${topSuggestions.length} suggestions with smart selection`);
      
      if (onProgress) {
        const finalMessage = errorCount > 0
          ? `Complete! Found ${topSuggestions.length} suggestions from ${suggestions.length} total (${errorCount} analysis errors)`
          : `Complete! Found ${topSuggestions.length} suggestions from ${suggestions.length} total`;
          
        onProgress({
          current: combinations.length,
          total: combinations.length,
          message: finalMessage
        });
      }
      
      return topSuggestions;

    } catch (error) {
      // Enhanced error handling with user-friendly messages
      console.error('[AutoSchedulingService] Error in generateSuggestions:', error);
      
      if (error instanceof Error) {
        // Re-throw with enhanced context if it's already a user-friendly error
        if (error.message.includes('Please') || error.message.includes('Try')) {
          throw error;
        }
        
        // Enhance generic errors with helpful context
        throw new Error(`Suggestion generation failed: ${error.message}\n\nPlease check your parameters and try again. If the problem persists, try narrowing your search criteria.`);
      }
      
      // Handle unknown errors
      throw new Error('An unexpected error occurred during suggestion generation. Please try again with different parameters.');
    }
  }

  /**
   * Validate context data (teachers, groups, subjects, schedule items)
   */
  private validateContextData(
    baseScheduleItems: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    parameters: SuggestionParameters
  ): void {
    // Validate base schedule items
    if (!Array.isArray(baseScheduleItems)) {
      throw new Error('Schedule data is invalid. Please refresh the page and try again.');
    }

    // Validate teachers array and find the specified teacher
    if (!Array.isArray(teachers) || teachers.length === 0) {
      throw new Error('No teachers are available. Please ensure teacher data is loaded.');
    }

    const teacher = teachers.find(t => t.id?.toString() === parameters.teacher);
    if (!teacher) {
      throw new Error(`Selected teacher (ID: ${parameters.teacher}) is not available. Please select a different teacher or refresh the data.`);
    }

    // Validate groups array and find the specified groups
    if (!Array.isArray(groups) || groups.length === 0) {
      throw new Error('No groups are available. Please ensure group data is loaded.');
    }

    const missingGroups = parameters.groups.filter(groupId => 
      !groups.some(g => g.id?.toString() === groupId.toString())
    );
    if (missingGroups.length > 0) {
      throw new Error(`Selected groups are not available: ${missingGroups.join(', ')}. Please select different groups or refresh the data.`);
    }

    // Validate subjects array and find the specified subject
    if (!Array.isArray(subjects) || subjects.length === 0) {
      throw new Error('No subjects are available. Please ensure subject data is loaded.');
    }

    const subject = subjects.find(s => s.id === parameters.subject || s.name === parameters.subject);
    if (!subject) {
      throw new Error(`Selected subject (${parameters.subject}) is not available. Please select a different subject or refresh the data.`);
    }

    // Validate that rooms exist (basic check)
    if (parameters.rooms.some(room => !room || room.trim() === '')) {
      throw new Error('Some selected rooms are invalid. Please check your room selection.');
    }
  }

  /**
   * Generate week patterns (combinations) of size weeksToSchedule from availableWeeks.
   * Weeks do not need to be consecutive.
   */
  private generateWeekPatterns(availableWeeks: number[], weeksToSchedule: number): number[][] {
    const uniqueSortedWeeks = Array.from(new Set(availableWeeks)).sort((a, b) => a - b);
    if (weeksToSchedule <= 1) {
      return uniqueSortedWeeks.map(week => [week]);
    }
    if (weeksToSchedule > uniqueSortedWeeks.length) {
      return [];
    }
    const results: number[][] = [];
    const current: number[] = [];
    const MAX_PATTERNS = this.MAX_SUGGESTIONS; // cap to avoid combinatorial explosion

    const backtrack = (startIndex: number) => {
      if (results.length >= MAX_PATTERNS) return;
      if (current.length === weeksToSchedule) {
        results.push([...current]);
        return;
      }
      for (let i = startIndex; i < uniqueSortedWeeks.length; i++) {
        if (results.length >= MAX_PATTERNS) return;
        // Prune if remaining elements are insufficient
        if (uniqueSortedWeeks.length - i < weeksToSchedule - current.length) {
          break;
        }
        current.push(uniqueSortedWeeks[i]!);
        backtrack(i + 1);
        current.pop();
      }
    };

    backtrack(0);
    return results;
  }

  /**
   * Analyze a week pattern for conflicts across all weeks in the pattern
   */
  private async analyzeWeekPattern(
    day: string,
    timeStart: string,
    timeEnd: string,
    room: string,
    weekPattern: number[],
    parameters: SuggestionParameters,
    context: AnalysisContext
  ): Promise<ScheduleSuggestion> {
    let totalConflictScore = 0;
    const allConflicts: any[] = [];
    
    // Analyze each week in the pattern
    for (const week of weekPattern) {
      const weekSuggestion = await this.analyzeTimeSlot(
        day,
        timeStart,
        timeEnd,
        room,
        week,
        parameters,
        context
      );
      
      totalConflictScore += weekSuggestion.conflictScore;
      allConflicts.push(...weekSuggestion.conflicts);
    }

    // Create a combined suggestion representing the entire pattern
    const suggestion: ScheduleSuggestion = {
      id: `suggestion-pattern-${weekPattern.join('-')}-${day}-${timeStart}-${room}`,
      day,
      timeStart,
      timeEnd,
      room,
      week: weekPattern[0] || 1, // Use first week as representative, fallback to 1
      weeks: weekPattern,
      year: parameters.year,
      conflictScore: totalConflictScore,
      conflicts: allConflicts,
      isOptimal: totalConflictScore === 0
    };

    return suggestion;
  }

  /**
   * Analyze a specific time slot for conflicts
   */
  private async analyzeTimeSlot(
    day: string,
    timeStart: string,
    timeEnd: string,
    room: string,
    week: number,
    parameters: SuggestionParameters,
    context: AnalysisContext
  ): Promise<ScheduleSuggestion> {
    // Resolve teacher and groups to names for accurate conflict detection
    const resolvedTeacher = this.resolveTeacherName(parameters.teacher, context.teachers);
    const resolvedGroups = this.resolveGroupNames(parameters.groups, context.groups);

    // Create a temporary schedule entry for analysis
    // Use the excludeItemId as the ID so conflict detection can properly exclude the current entry
    const tempEntry = new ScheduleEntry({
      id: parameters.excludeItemId || `temp-${Date.now()}-${Math.random()}`,
      teacher: resolvedTeacher,
      subject: parameters.subject,
      course: parameters.subject, // ensure subject-room constraint can be evaluated by name
      groups: resolvedGroups,
      room,
      day,
      timeStart,
      timeEnd,
      weekSeries: week.toString(),
      year: parameters.year,
      type: 'class'
    });

    // Check availability using the existing AvailabilityService
    const startMinutes = tempEntry.startMinutes;
    if (startMinutes === null) {
      throw new Error('Invalid time format');
    }

    // Build spatial index once per (week, year) for optimized checks
    const indexKey = `${week}-${parameters.year}`;
    if (!this.builtIndexWeeks.has(indexKey)) {
      try {
        availabilityService.buildSpatialIndexForWeek(
          context.baseScheduleItems,
          week,
          parameters.year
        );
        this.builtIndexWeeks.add(indexKey);
      } catch (e) {
        console.warn('[AutoSchedulingService] Failed to build spatial index, proceeding without it:', e);
      }
    }

    const availabilityResult = availabilityService.checkAvailabilityOptimized(
      tempEntry,
      room,
      day,
      startMinutes,
      context.baseScheduleItems,
      context.teachers,
      context.groups,
      context.subjects,
      week,
      parameters.year
    );

    // Calculate conflict score
    const { score, conflicts } = this.suggestionScoringService.calculateConflictScore(availabilityResult);

    // Create suggestion
    const suggestion: ScheduleSuggestion = {
      id: `suggestion-${week}-${day}-${timeStart}-${room}`,
      day,
      timeStart,
      timeEnd,
      room,
      week,
      year: parameters.year,
      conflictScore: score,
      conflicts,
      isOptimal: score === 0
    };

    return suggestion;
  }

  /**
   * Resolve teacher input (which may be an ID string) to a teacher name
   */
  private resolveTeacherName(teacherInput: string, teachers: Teacher[]): string {
    if (!teacherInput) return '';
    // Exact name match first
    const byName = teachers.find(t => t.name === teacherInput);
    if (byName?.name) return byName.name;
    // Match by ID string
    const byId = teachers.find(t => t.id?.toString() === teacherInput);
    if (byId?.name) return byId.name;
    // Fallback to input as-is
    return teacherInput;
  }

  /**
   * Resolve group identifiers (IDs or names) to group names to align with existing schedule items
   */
  private resolveGroupNames(groupInputs: (number | string)[], groups: Group[]): (string | number | { id: number; name?: string; group_code?: string; })[] {
    if (!Array.isArray(groupInputs)) return [];
    return groupInputs.map(input => {
      // If already a string, attempt to resolve to canonical name if possible
      if (typeof input === 'string') {
        const byName = groups.find(g => g.name === input || (g as any).group_code === input || g.id?.toString() === input);
        return byName?.name || (byName as any)?.group_code || input;
      }
      // If number (id), resolve to name or code
      const byId = groups.find(g => g.id === input);
      if (byId) return byId.name || (byId as any).group_code || byId.id;
      return input;
    });
  }

  /**
   * Generate time slots within the specified hour range
   */
  private generateTimeSlots(minHour: number, maxHour: number, duration: number): TimeSlot[] {
    const timeSlots: TimeSlot[] = [];
    const intervalMinutes = 30; // 30-minute intervals

    // Convert hours to minutes
    const startMinutes = minHour * 60;
    const endMinutes = maxHour * 60;

    // Generate slots
    for (let currentMinutes = startMinutes; currentMinutes + duration <= endMinutes; currentMinutes += intervalMinutes) {
      const endSlotMinutes = currentMinutes + duration;

      const timeSlot: TimeSlot = {
        timeStart: formatTimeFromMinutes(currentMinutes),
        timeEnd: formatTimeFromMinutes(endSlotMinutes),
        startMinutes: currentMinutes,
        endMinutes: endSlotMinutes
      };

      timeSlots.push(timeSlot);
    }

    return timeSlots;
  }

  /**
   * Rank suggestions by score, time, and room
   */
  private rankSuggestions(suggestions: ScheduleSuggestion[]): ScheduleSuggestion[] {
    return suggestions.sort((a, b) => {
      // Primary sort: conflict score (ascending - lower is better)
      if (a.conflictScore !== b.conflictScore) {
        return a.conflictScore - b.conflictScore;
      }

      // Secondary sort: time (ascending - earlier is better)
      const aTimeMinutes = this.timeToMinutes(a.timeStart);
      const bTimeMinutes = this.timeToMinutes(b.timeStart);
      if (aTimeMinutes !== bTimeMinutes) {
        return aTimeMinutes - bTimeMinutes;
      }

      // Tertiary sort: room name (alphabetical)
      return a.room.localeCompare(b.room);
    });
  }

  /**
   * Convert time string to minutes for sorting
   */
  private timeToMinutes(timeString: string): number {
    const timeParts = timeString.split(':').map(Number);
    if (timeParts.length === 2) {
      const hours = timeParts[0];
      const minutes = timeParts[1];
      if (typeof hours === 'number' && typeof minutes === 'number' && 
          !isNaN(hours) && !isNaN(minutes)) {
        return hours * 60 + minutes;
      }
    }
    // Fallback to 0 if parsing fails
    return 0;
  }

  /**
   * Generate suggestions with optimized performance for large parameter sets
   */
  async generateSuggestionsOptimized(
    parameters: SuggestionParameters,
    baseScheduleItems: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    onProgress?: (progress: { current: number; total: number; message: string }) => void
  ): Promise<ScheduleSuggestion[]> {
    console.log('[AutoSchedulingService] Generating optimized suggestions');

    // Validate parameters
    this.validateParameters(parameters);

    // Filter out the current item being edited to avoid self-conflicts
    const filteredBaseScheduleItems = parameters.excludeItemId 
      ? baseScheduleItems.filter(item => item.id?.toString() !== parameters.excludeItemId?.toString())
      : baseScheduleItems;

    if (parameters.excludeItemId) {
      const excludedCount = baseScheduleItems.length - filteredBaseScheduleItems.length;
      console.log(`[AutoSchedulingService] Filtered out ${excludedCount} item(s) with ID ${parameters.excludeItemId} to avoid self-conflicts`);
    }

    // Pre-filter rooms based on subject constraints for better performance
    const filteredRooms = this.filterRoomsBySubject(parameters.rooms, parameters.subject, subjects);
    const optimizedParameters = { ...parameters, rooms: filteredRooms };

    // Use early termination if we find enough optimal suggestions
    return this.generateSuggestionsWithEarlyTermination(
      optimizedParameters,
      filteredBaseScheduleItems, // Pass filtered base schedule items
      teachers,
      groups,
      subjects,
      onProgress
    );
  }

  /**
   * Generate suggestions with early termination when enough optimal suggestions are found
   */
  private async generateSuggestionsWithEarlyTermination(
    parameters: SuggestionParameters,
    baseScheduleItems: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    onProgress?: (progress: { current: number; total: number; message: string }) => void
  ): Promise<ScheduleSuggestion[]> {
    const timeSlots = this.generateTimeSlots(
      parameters.minHour,
      parameters.maxHour,
      parameters.duration
    );

    const weekPatterns = this.generateWeekPatterns(parameters.weeks, parameters.weeksToSchedule);

    // Filter out the current item being edited to avoid self-conflicts
    const filteredBaseScheduleItems = parameters.excludeItemId 
      ? baseScheduleItems.filter(item => item.id?.toString() !== parameters.excludeItemId?.toString())
      : baseScheduleItems;

    if (parameters.excludeItemId) {
      const excludedCount = baseScheduleItems.length - filteredBaseScheduleItems.length;
      console.log(`[AutoSchedulingService] Filtered out ${excludedCount} item(s) with ID ${parameters.excludeItemId} to avoid self-conflicts`);
    }

    const suggestions: ScheduleSuggestion[] = [];
    const optimalSuggestions: ScheduleSuggestion[] = [];
    const context: AnalysisContext = {
      baseScheduleItems: filteredBaseScheduleItems,
      teachers,
      groups,
      subjects
    };

    const totalCombinations = weekPatterns.length * 
                             parameters.days.length * 
                             timeSlots.length * 
                             parameters.rooms.length;

    let processedCount = 0;
    const TARGET_OPTIMAL_SUGGESTIONS = 5; // Stop early if we find enough optimal suggestions

    // Process in order of preference (earlier times first)
    for (const weekPattern of weekPatterns) {
      for (const day of parameters.days) {
        for (const timeSlot of timeSlots) {
          for (const room of parameters.rooms) {
            try {
              const suggestion = await this.analyzeWeekPattern(
                day,
                timeSlot.timeStart,
                timeSlot.timeEnd,
                room,
                weekPattern,
                parameters,
                context
              );

              suggestions.push(suggestion);

              // Track optimal suggestions for early termination
              if (suggestion.isOptimal) {
                optimalSuggestions.push(suggestion);
                
                // Early termination if we have enough optimal suggestions
                if (optimalSuggestions.length >= TARGET_OPTIMAL_SUGGESTIONS) {
                  console.log(`[AutoSchedulingService] Early termination: found ${optimalSuggestions.length} optimal suggestions`);
                  
                  if (onProgress) {
                    onProgress({
                      current: totalCombinations,
                      total: totalCombinations,
                      message: `Found ${optimalSuggestions.length} optimal suggestions`
                    });
                  }
                  
                  return this.rankSuggestions(optimalSuggestions).slice(0, 10);
                }
              }

              processedCount++;

              // Report progress periodically
              if (processedCount % this.BATCH_SIZE === 0 && onProgress) {
                onProgress({
                  current: processedCount,
                  total: Math.min(totalCombinations, this.MAX_SUGGESTIONS),
                  message: `Analyzed ${processedCount} patterns, found ${optimalSuggestions.length} optimal`
                });
              }

              // Yield control periodically
              if (processedCount % this.BATCH_SIZE === 0) {
                await new Promise(resolve => setTimeout(resolve, 0));
              }

              // Stop if we exceed the maximum limit
              if (processedCount >= this.MAX_SUGGESTIONS) {
                console.warn(`[AutoSchedulingService] Reached maximum suggestions limit`);
                break;
              }

            } catch (error) {
              console.error('[AutoSchedulingService] Error analyzing week pattern:', error);
              processedCount++;
            }
          }
          if (processedCount >= this.MAX_SUGGESTIONS) break;
        }
        if (processedCount >= this.MAX_SUGGESTIONS) break;
      }
      if (processedCount >= this.MAX_SUGGESTIONS) break;
    }

    // Return all suggestions if we didn't find enough optimal ones
    const sortedSuggestions = this.rankSuggestions(suggestions);
    const topSuggestions = sortedSuggestions.slice(0, 10);
    
    if (onProgress) {
      onProgress({
        current: processedCount,
        total: Math.min(totalCombinations, this.MAX_SUGGESTIONS),
        message: `Complete! Found ${topSuggestions.length} suggestions`
      });
    }
    
    return topSuggestions;
  }

  /**
   * Filter rooms based on subject constraints
   */
  private filterRoomsBySubject(rooms: string[], subjectName: string, subjects: Subject[]): string[] {
    const subject = subjects.find(s => s.name === subjectName);
    
    if (!subject || !subject.allowedRooms || subject.allowedRooms.length === 0) {
      // No constraints, return all rooms
      return rooms;
    }

    // Filter rooms based on subject constraints
    const allowedRoomNames = subject.allowedRooms; // Already an array of room names
    return rooms.filter(room => allowedRoomNames.includes(room));
  }

  /**
   * Validate suggestion parameters with comprehensive error messages
   */
  private validateParameters(parameters: SuggestionParameters): void {
    // Validate weeks
    if (!parameters.weeks || parameters.weeks.length === 0) {
      throw new Error('Please select at least one week for scheduling. Use the week picker to choose your preferred weeks.');
    }

    // Validate week numbers are reasonable
    const invalidWeeks = parameters.weeks.filter(week => week < 1 || week > 53);
    if (invalidWeeks.length > 0) {
      throw new Error(`Invalid week numbers detected: ${invalidWeeks.join(', ')}. Week numbers must be between 1 and 53.`);
    }

    // Validate days
    if (!parameters.days || parameters.days.length === 0) {
      throw new Error('Please select at least one day of the week. Click on the day buttons to choose your preferred days.');
    }

    // Validate day names
    const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const invalidDays = parameters.days.filter(day => !validDays.includes(day.toLowerCase()));
    if (invalidDays.length > 0) {
      throw new Error(`Invalid day names detected: ${invalidDays.join(', ')}. Valid days are: ${validDays.join(', ')}.`);
    }

    // Validate time range
    if (typeof parameters.minHour !== 'number' || typeof parameters.maxHour !== 'number') {
      throw new Error('Time range must be specified with valid hour numbers.');
    }

    if (parameters.minHour >= parameters.maxHour) {
      throw new Error(`Invalid time range: ${parameters.minHour}:00 to ${parameters.maxHour}:00. The start time must be earlier than the end time.`);
    }

    if (parameters.minHour < 0 || parameters.maxHour > 24) {
      throw new Error(`Time range must be within 0-24 hours. Current range: ${parameters.minHour}:00 to ${parameters.maxHour}:00.`);
    }

    // Validate time range is sufficient for duration
    const timeRangeMinutes = (parameters.maxHour - parameters.minHour) * 60;
    if (timeRangeMinutes < parameters.duration) {
      throw new Error(`Time range (${timeRangeMinutes} minutes) is too short for the class duration (${parameters.duration} minutes). Please expand your time range or reduce the class duration.`);
    }

    // Validate rooms
    if (!parameters.rooms || parameters.rooms.length === 0) {
      throw new Error('Please select at least one room. If no rooms are selected, all available rooms will be considered.');
    }

    // Validate room names are not empty
    const emptyRooms = parameters.rooms.filter(room => !room || room.trim() === '');
    if (emptyRooms.length > 0) {
      throw new Error('Some selected rooms have empty names. Please check your room selection.');
    }

    // Validate duration
    if (!parameters.duration || parameters.duration <= 0) {
      throw new Error('Class duration must be greater than 0 minutes. Please check your schedule entry settings.');
    }

    if (parameters.duration > 480) { // 8 hours
      throw new Error(`Class duration (${parameters.duration} minutes) seems unusually long. Please verify this is correct.`);
    }

    // Validate teacher
    if (!parameters.teacher || parameters.teacher.trim() === '') {
      throw new Error('Teacher must be specified. Please select a teacher from the schedule entry form.');
    }

    // Validate subject
    if (!parameters.subject || parameters.subject.trim() === '') {
      throw new Error('Subject must be specified. Please select a subject from the schedule entry form.');
    }

    // Validate groups
    if (!parameters.groups || parameters.groups.length === 0) {
      throw new Error('At least one group must be specified. Please select groups from the schedule entry form.');
    }

    // Validate group IDs are not empty
    const emptyGroups = parameters.groups.filter(group => !group || group.toString().trim() === '');
    if (emptyGroups.length > 0) {
      throw new Error('Some selected groups have empty IDs. Please check your group selection.');
    }

    // Validate weeksToSchedule
    if (!parameters.weeksToSchedule || parameters.weeksToSchedule <= 0) {
      throw new Error('Weeks to schedule must be a positive number. This determines how many consecutive weeks the class will run.');
    }

    if (parameters.weeksToSchedule > parameters.weeks.length) {
      throw new Error(`Cannot schedule for ${parameters.weeksToSchedule} weeks when only ${parameters.weeks.length} eligible weeks are selected. Please select more weeks or reduce the number of weeks to schedule.`);
    }

    // Validate year
    if (!parameters.year || parameters.year < 2000 || parameters.year > 2100) {
      throw new Error(`Invalid year: ${parameters.year}. Year must be between 2000 and 2100.`);
    }

    // Performance validation with detailed feedback (estimate without enumerating all week combinations)
    const n = new Set(parameters.weeks).size;
    const k = Math.min(parameters.weeksToSchedule, n);
    const weekPatternCount = this.estimateCombinationCount(n, k);
    const timeSlots = Math.ceil((parameters.maxHour - parameters.minHour) * 2); // 30-minute intervals
    const totalCombinations = weekPatternCount * 
                             parameters.days.length * 
                             timeSlots * 
                             parameters.rooms.length;

    // if (totalCombinations > this.MAX_SUGGESTIONS * 5) {
    //   throw new Error(`Search parameters are too broad (${totalCombinations.toLocaleString()} combinations to analyze). Please narrow your selection:\n• Select fewer weeks (currently ${parameters.weeks.length})\n• Select fewer days (currently ${parameters.days.length})\n• Reduce time range (currently ${parameters.maxHour - parameters.minHour} hours)\n• Select fewer rooms (currently ${parameters.rooms.length})`);
    // }

    if (totalCombinations > this.MAX_SUGGESTIONS * 2) {
      console.warn(`[AutoSchedulingService] Large parameter set detected (${totalCombinations.toLocaleString()} combinations). This may take longer to process.`);
    }
  }
  
  /**
   * Estimate number of combinations C(n, k) without enumerating
   */
  private estimateCombinationCount(n: number, k: number): number {
    if (k < 0 || n < 0 || k > n) return 0;
    k = Math.min(k, n - k);
    if (k === 0) return 1;
    let result = 1;
    for (let i = 1; i <= k; i++) {
      result = (result * (n - k + i)) / i;
      if (!isFinite(result)) return Number.MAX_SAFE_INTEGER;
    }
    return Math.round(result);
  }

  /**
   * Smart selection: Pick top suggestions with random selection among similar low-conflict options
   * This ensures variety while maintaining quality by prioritizing low conflict scores
   */
  private selectTopSuggestionsWithRandomization(suggestions: ScheduleSuggestion[], count: number): ScheduleSuggestion[] {
    if (suggestions.length <= count) {
      return suggestions;
    }

    // Group suggestions by conflict score
    const groupedSuggestions = new Map<number, ScheduleSuggestion[]>();
    suggestions.forEach(s => {
      const score = s.conflictScore;
      if (!groupedSuggestions.has(score)) {
        groupedSuggestions.set(score, []);
      }
      const group = groupedSuggestions.get(score)!;
      group.push(s);
    });

    const selectedSuggestions: ScheduleSuggestion[] = [];
    const scores = Array.from(groupedSuggestions.keys()).sort((a, b) => a - b); // Sort scores ascending

    // First, try to get suggestions from the lowest conflict scores
    for (const score of scores) {
      const group = groupedSuggestions.get(score)!;
      if (group.length > 0) {
        // If we have enough suggestions in this group to fill our quota
        if (selectedSuggestions.length + group.length <= count) {
          // Add all suggestions from this group
          selectedSuggestions.push(...group);
        } else {
          // We need to randomly select some from this group
          const remainingSlots = count - selectedSuggestions.length;
          const shuffled = [...group].sort(() => Math.random() - 0.5);
          selectedSuggestions.push(...shuffled.slice(0, remainingSlots));
        }

        if (selectedSuggestions.length >= count) {
          break;
        }
      }
    }

    // Ensure we return exactly the requested number of suggestions
    return selectedSuggestions.slice(0, count);
  }
}

export default AutoSchedulingService;